import React, {useMemo, useState} from 'react';
import axios from 'axios';
import {Button, Header, Spinner} from '@amzn/awsui-components-react';
import {F3ExcelsiorForecastStoreLambda} from '@amzn/f3-excelsior-forecast-store-lambda';
import {FileRejection, useDropzone} from 'react-dropzone';
import {Notification} from '../navigation/page-layout';
import {RegionId, ServiceResource, useAuth} from '../hooks/use-auth';
import '../../assets/styles/file-download-upload.scss';

const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// The OTE exclusion operations resolve on the client at runtime. This shim describes their
// signatures so the calls below stay typed against the request/response shapes.
interface OteExclusionOperations {
    downloadOteExclusionTemplate(input: Record<string, never>): {promise(): Promise<{downloadUrl: string}>};
    prepareOteExclusionUpload(input: {fileName: string}): {
        promise(): Promise<{uploadUrl: string; s3Key: string; uploadId: string}>;
    };
    processOteExclusionUpload(input: {s3Key: string; uploadId: string}): {promise(): Promise<{exclusionCount: number}>};
}

export function OteExclusionManager(props: {pushNotification: (notification: Notification) => void}) {
    const auth = useAuth();
    const [isDownloading, setIsDownloading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const clientConfig = auth.authInformation!.getEndpoints()[RegionId.EU][ServiceResource.ForecastStoreEdit];
    const forecastStoreClient = useMemo(
        () => new F3ExcelsiorForecastStoreLambda(clientConfig) as F3ExcelsiorForecastStoreLambda & OteExclusionOperations,
        [clientConfig]
    );

    const handleDownloadTemplate = async () => {
        setIsDownloading(true);
        try {
            const {downloadUrl} = await forecastStoreClient.downloadOteExclusionTemplate({}).promise();
            window.open(downloadUrl, '_blank');
        } catch (error: any) {
            props.pushNotification({
                type: 'error',
                content: `Download failed: ${error.message || 'Unknown error'}`,
            });
        } finally {
            setIsDownloading(false);
        }
    };

    const onDrop = async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
        if (fileRejections.length > 0) {
            props.pushNotification({
                type: 'error',
                content: 'File rejected. Please upload an .xlsx file.',
            });
            return;
        }
        if (acceptedFiles.length === 0) return;

        const file = acceptedFiles[0];
        setIsUploading(true);

        try {
            const prepareResponse = await forecastStoreClient.prepareOteExclusionUpload({fileName: file.name}).promise();

            await axios.put(prepareResponse.uploadUrl, file, {
                headers: {
                    'Content-Type': file.type || XLSX_CONTENT_TYPE,
                    'x-amz-server-side-encryption': 'aws:kms',
                },
            });

            const processResponse = await forecastStoreClient
                .processOteExclusionUpload({
                    s3Key: prepareResponse.s3Key,
                    uploadId: prepareResponse.uploadId,
                })
                .promise();

            props.pushNotification({
                type: 'success',
                content: `Exclusions uploaded successfully. ${processResponse.exclusionCount} rows marked for exclusion.`,
            });
        } catch (error: any) {
            props.pushNotification({
                type: 'error',
                content: `Upload failed: ${error.message || 'Unknown error'}`,
            });
        } finally {
            setIsUploading(false);
        }
    };

    const {getRootProps, getInputProps} = useDropzone({
        onDrop,
        maxFiles: 1,
        accept: `.xlsx,${XLSX_CONTENT_TYPE}`,
        disabled: isUploading,
    });

    return (
        <div className="contentSignals">
            <div className="center">
                <div className="centered-header">
                    <Header variant="h1">OTE EXCLUSIONS</Header>
                </div>
                <p>
                    Download the pre-filled XLSX template, mark each anomaly Yes or No in the Exclude column, and upload the
                    completed file.
                </p>
                <br />
                <Button data-testid="download-button" variant="normal" loading={isDownloading} onClick={handleDownloadTemplate}>
                    OTE Exclusions Template
                </Button>
                <div className="uploadsContainer" style={{height: 350}}>
                    <div className="upload">
                        <div {...getRootProps()}>
                            <input {...getInputProps()} />
                            {isUploading ? (
                                <p>
                                    <Spinner size="large" />
                                </p>
                            ) : (
                                <p>
                                    Drag and drop your completed .xlsx file here, or click to select it.
                                    <br />
                                    <small>
                                        Your marked exclusions will be uploaded and applied to the kept OTE anomalies view.
                                    </small>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
